import type { BoxProps } from '@chakra-ui/react';
import { Box, Flex, Icon, Link, Text } from '@chakra-ui/react';
import { useLanguage } from '@ui/i18n';
import NextLink from '@ui/overrides/next/link';
import { Close } from '@veno-app/icons';
import type { ImageProps as NextImageProps } from 'next/image';
import NextImage from 'next/image';
import type { ReactNode } from 'react';
import React from 'react';

import { Card } from '../Card';

export type UpdateBannerProps = {
  mainImg: string | ReactNode;
  mainImgProps?: Omit<NextImageProps, 'src'>;
  text: ReactNode;
  secondaryText: string;
  link: string;
  linkText: ReactNode;
  isLinkExternal?: boolean;
  bgImage?: string;
  backgroundSize?: BoxProps['backgroundSize'];
  onClose: () => void;
};

function UpdateBanner({
  mainImg,
  mainImgProps,
  text,
  secondaryText,
  link,
  isLinkExternal,
  backgroundSize,
  bgImage,
  onClose,
  linkText,
}: UpdateBannerProps) {
  const language = useLanguage();
  const smallText = language.lang === 'ko' || language.lang === 'it';

  return (
    <Card
      h="124px"
      mx={0}
      p={4}
      bgPos="right"
      bgImage={bgImage}
      backgroundSize={backgroundSize}
      backgroundImage={bgImage}
      backgroundRepeat="no-repeat"
      flexDir="column"
      display="flex"
    >
      <Flex justify="space-between">
        <Box>
          {typeof mainImg === 'string' ? (
            <NextImage alt="banner logo" src={mainImg} {...mainImgProps} />
          ) : (
            <Box textStyle="h3" fontWeight="bold" color="brand-primary">
              {mainImg}
            </Box>
          )}
        </Box>
        <Icon as={Close} width={6} height={6} onClick={onClose} />
      </Flex>
      <Box flexGrow={1} />
      <Box>
        <Box textStyle={smallText ? 'bodySmall' : 'body'}>{text}</Box>
      </Box>
      <Flex mt={0.5} justify="space-between" alignItems="center">
        <Text
          textStyle={smallText ? 'caption' : 'bodySmall'}
          color="text.light"
          flexShrink="1"
          noOfLines={2}
        >
          {secondaryText}
        </Text>
        <NextLink href={link} passHref>
          <Link
            textStyle="textLink"
            isExternal={isLinkExternal}
            color="text.link"
            flexShrink="0"
          >
            {linkText}
          </Link>
        </NextLink>
      </Flex>
    </Card>
  );
}

export default UpdateBanner;
